import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/yc/DashboardShell";
import { HealthDial, SubScoreBar } from "@/components/yc/HealthDial";
import { api } from "@/lib/api";
import { specLabel } from "@/lib/specialisms";
import { Lock, Activity, Users, Briefcase, Heart, Share2, Bookmark, Eye, TrendingUp, Sparkles } from "lucide-react";

const BrandDashboard = () => {
    const [access, setAccess] = useState(null);
    const [campaigns, setCampaigns] = useState([]);
    const [health, setHealth] = useState(null);
    const [insights, setInsights] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/brand/access").then((r) => setAccess(r.data));
        api.get("/brand/campaigns").then((r) => setCampaigns(r.data));
        api.get("/brand/health-score").then((r) => setHealth(r.data));
        api.get("/brand/insights").then((r) => setInsights(r.data));
    }, []);

    const isActive = access?.unlocked;
    const videosTotal = access?.videos?.total ?? 0;
    const perf = insights?.performance || {};

    return (
        <DashboardShell role="brand" title="Brand dashboard" subtitle="Plan · Videos · Health Score · Campaigns" actions={
            <Link to="/brand/campaigns/new" data-testid="dash-new-campaign" className="yc-btn-primary text-sm py-2 px-4">New campaign</Link>
        }>
            {/* Demo mode banner */}
            {access?.is_demo && (
                <div className="yc-card p-4 bg-yc-pink text-white flex items-center gap-3" data-testid="demo-mode-banner">
                    <Sparkles className="w-5 h-5 shrink-0" />
                    <div className="text-sm font-semibold">
                        You&apos;re exploring a demo — the creators here are fictional and matching is instant. Ready for the real network?{" "}
                        <a href="mailto:info@curated-chaos.co.uk" className="underline">Talk to us</a>.
                    </div>
                </div>
            )}
            {/* Plan & videos banner */}
            <div className="yc-card p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full border-2 border-yc-ink flex items-center justify-center ${isActive ? "bg-yc-blue text-white" : "bg-yc-cream"}`}>
                        {isActive ? <Activity className="w-6 h-6" /> : <Lock className="w-6 h-6" />}
                    </div>
                    <div>
                        <div className="yc-overline">Plan</div>
                        <div className="font-heading text-2xl font-bold tracking-tight" data-testid="membership-status">
                            {access?.plan_status === "active" ? `${access?.plan_name}` : access?.taster_valid ? "Taster" : "Free member"}
                        </div>
                        <div className="text-xs text-yc-ink/60" data-testid="dash-videos-left">
                            {videosTotal} video{videosTotal === 1 ? "" : "s"} in your allowance
                            {access?.plan_renews_at && access?.plan_status === "active" && ` · renews ${new Date(access.plan_renews_at).toLocaleDateString()}`}
                        </div>
                    </div>
                </div>
                <div className="flex gap-3">
                    <Link to="/brand/membership" data-testid="dash-manage-membership" className="yc-btn-secondary text-sm py-2 px-4">Plan & Videos</Link>
                    {!isActive && <Link to="/brand/membership" data-testid="dash-activate-membership" className="yc-btn-primary text-sm py-2 px-4">Start a plan</Link>}
                </div>
            </div>

            {/* Locked preview */}
            {access && !isActive && (
                <div className="yc-card p-8 bg-yc-blue text-white">
                    <div className="yc-overline text-white">Free member view</div>
                    <h3 className="font-heading text-3xl font-bold mt-2">Browse free. Unlock when you launch.</h3>
                    <p className="text-white/90 mt-3 max-w-2xl">As a free member you can browse the vetted network — niche, location, reach and health scores. Creator names, handles and portfolios unlock when you start a campaign plan or book a £125 taster.</p>
                </div>
            )}

            {/* Health score */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {health?.locked ? (
                    <div className="yc-card p-8 lg:col-span-3 bg-yc-pink text-white">
                        <div className="yc-overline text-white">Curated Health Score · Locked</div>
                        <h3 className="font-heading text-3xl font-bold mt-2">Exclusive to Corporate clients.</h3>
                        <p className="text-white/90 mt-3 max-w-2xl">{health.message}</p>
                        <Link to="/brand/campaigns/new" data-testid="upgrade-corporate-cta" className="yc-btn-primary mt-6 bg-white text-yc-ink inline-flex">Brief a Corporate campaign →</Link>
                    </div>
                ) : (
                    <>
                        <div className="yc-card p-8 flex flex-col items-center justify-center">
                            <HealthDial score={health?.overall || 0} />
                        </div>
                        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-3">
                            {health && ["consistency", "performance", "audience_fit", "brand_safety", "freshness"].map((k) => (
                                <SubScoreBar key={k} label={k} value={health[k]} hint={health.guidance?.[k]} />
                            ))}
                        </div>
                    </>
                )}
            </div>

            {/* Campaigns + quick links */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="yc-card-sm p-6 lg:col-span-2">
                    <div className="flex items-center justify-between">
                        <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Briefcase className="w-5 h-5 text-yc-blue" /> Campaigns</h3>
                        <Link to="/brand/campaigns" className="text-sm font-bold text-yc-blue underline">View all</Link>
                    </div>
                    <div className="mt-4 divide-y-2 divide-yc-ink/10">
                        {campaigns.length === 0 && <div className="py-8 text-center text-yc-ink/60">No campaigns yet. Brief your first one.</div>}
                        {campaigns.slice(0, 5).map((c) => (
                            <div key={c.campaign_id} className="py-3 flex items-center justify-between" data-testid={`campaign-row-${c.campaign_id}`}>
                                <div>
                                    <div className="font-semibold">{c.title}</div>
                                    <div className="text-xs text-yc-ink/60">{c.videos_count ? `${c.videos_count} video${c.videos_count === 1 ? "" : "s"}` : c.package_id ? `Package: ${c.package_id}` : ""} · Status: {c.status}</div>
                                </div>
                                <span className="yc-badge bg-yc-cream">{c.status}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="yc-card-sm p-6">
                    <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-yc-pink" /> Roster</h3>
                    <p className="text-sm text-yc-ink/70 mt-2">Browse vetted creators by specialism, location and audience — free for all members.</p>
                    <Link to="/brand/roster" data-testid="dash-open-roster" className="yc-btn-secondary w-full mt-4 text-sm py-2 px-3">Open roster</Link>
                </div>
            </div>

            {/* Video performance */}
            <div className="yc-card p-6" data-testid="brand-performance">
                <div className="flex items-center justify-between">
                    <h3 className="font-heading text-xl font-bold flex items-center gap-2"><TrendingUp className="w-5 h-5 text-yc-blue" /> Video performance</h3>
                    <span className="text-xs text-yc-ink/60">{insights?.videos_delivered || 0} videos delivered · {insights?.completed_campaigns || 0} campaigns complete</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
                    {[
                        { l: "Likes", v: perf.likes, i: Heart, tid: "perf-likes" },
                        { l: "Shares", v: perf.shares, i: Share2, tid: "perf-shares" },
                        { l: "Saves", v: perf.saves, i: Bookmark, tid: "perf-saves" },
                        { l: "Reach", v: perf.reach, i: Eye, tid: "perf-reach" },
                        { l: "Engagement", v: perf.engagement, i: Activity, tid: "perf-engagement" },
                    ].map((m) => {
                        const Icon = m.i;
                        return (
                            <div key={m.l} className="border-2 border-yc-ink rounded-2xl p-4 bg-yc-cream" data-testid={m.tid}>
                                <Icon className="w-5 h-5 text-yc-blue" />
                                <div className="yc-overline mt-2">{m.l}</div>
                                <div className="font-heading font-black text-2xl sm:text-3xl tracking-tighter mt-1 break-words">{(m.v || 0).toLocaleString()}</div>
                            </div>
                        );
                    })}
                </div>
                {(!insights?.completed_campaigns) && (
                    <p className="text-xs text-yc-ink/60 mt-4">Numbers populate once you complete your first placement and capture outcome data.</p>
                )}
            </div>

            {/* Suggested creators for next campaign */}
            <div className="yc-card p-6" data-testid="suggested-creators-section">
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <h3 className="font-heading text-xl font-bold flex items-center gap-2"><Users className="w-5 h-5 text-yc-pink" /> Suggested for your next campaign</h3>
                    {isActive && <Link to="/brand/campaigns/new" className="yc-btn-primary text-sm py-2 px-4" data-testid="suggested-brief-cta">Brief next campaign →</Link>}
                </div>
                <div className="mt-5 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {(insights?.suggested_creators || []).map((s) => (
                        <div key={s.creator_id} className="border-2 border-yc-ink rounded-2xl p-4 bg-white" data-testid={`suggested-${s.creator_id}`}>
                            <div className="font-heading font-bold text-lg">{s.name}</div>
                            <div className="flex flex-wrap gap-1 mt-2">
                                {(s.specialisms || []).slice(0, 3).map((x) => <span key={x} className="yc-badge bg-yc-cream text-xs">{specLabel(x)}</span>)}
                            </div>
                            <div className="grid grid-cols-3 gap-1 mt-3 text-xs">
                                <div><div className="yc-overline text-[10px]">Quality</div><div className="font-heading font-bold">{Math.round(s.quality_score)}</div></div>
                                <div><div className="yc-overline text-[10px]">Reliab.</div><div className="font-heading font-bold">{Math.round(s.reliability_score)}</div></div>
                                <div><div className="yc-overline text-[10px]">Reach</div><div className="font-heading font-bold">{(s.followers || 0).toLocaleString()}</div></div>
                            </div>
                            <p className="text-xs text-yc-ink/70 mt-3 leading-snug">{s.reason}</p>
                        </div>
                    ))}
                    {(insights?.suggested_creators || []).length === 0 && (
                        <div className="md:col-span-2 lg:col-span-4 text-center text-yc-ink/60 py-8 text-sm">Suggestions appear once we have approved creators in the network.</div>
                    )}
                </div>

                {(insights?.top_creators || []).length > 0 && (
                    <div className="mt-8">
                        <div className="yc-overline">Your top performers</div>
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {insights.top_creators.map((c) => (
                                <div key={c.creator_id} className="border-2 border-yc-ink rounded-xl p-3 bg-yc-cream flex items-center justify-between" data-testid={`top-${c.creator_id}`}>
                                    <div>
                                        <div className="font-bold text-sm">{c.name}</div>
                                        <div className="text-xs text-yc-ink/60">{c.placements} placement(s) · avg {c.avg_rating}/5</div>
                                    </div>
                                    <div className="font-heading font-black text-2xl">{Math.round(c.quality_score)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </DashboardShell>
    );
};

export default BrandDashboard;
